import { FEATURE_CHARACTER_URLS } from '../rendering/asset-manifest';
import { FEATURE_SUMMARY_AUTO_CLOSE_MS, getFeatureSummaryTier } from '../presentation/feature-presentation';
import type { FeatureOverlayPort } from '../presentation/feature-presentation';
import type { GridPosition } from '../rendering/types';
import { animateCentCount } from './count-up';
import { formatCents } from './format';

function requireElement<T extends Element>(root: ParentNode, selector: string): T {
  const element = root.querySelector<T>(selector);
  if (element === null) throw new Error(`Feature-Element fehlt: ${selector}`);
  return element;
}

async function waitForDecodedImage(image: HTMLImageElement): Promise<void> {
  if (!image.complete) {
    await new Promise<void>((resolve) => {
      image.addEventListener('load', () => resolve(), { once: true });
      image.addEventListener('error', () => resolve(), { once: true });
    });
  }
  try {
    await image.decode();
  } catch {
    // A failed optional decode must not leave the summary inaccessible. The
    // browser still renders the loaded image or its normal broken-image state.
  }
}

export class FeatureSummaryImageLoader {
  private readonly preloads = new Map<string, Promise<void>>();
  private requestToken = 0;

  constructor(urls: readonly string[], createImage: () => HTMLImageElement) {
    for (const url of new Set(urls)) {
      const image = createImage();
      image.decoding = 'async';
      image.src = url;
      this.preloads.set(url, waitForDecodedImage(image));
    }
  }

  async apply(target: HTMLImageElement, url: string): Promise<boolean> {
    const token = ++this.requestToken;
    await this.preloads.get(url);
    if (token !== this.requestToken) return false;
    target.decoding = 'async';
    target.src = url;
    await waitForDecodedImage(target);
    return token === this.requestToken;
  }

  invalidate(): void { this.requestToken += 1; }
}

export interface FeatureDialogOptions {
  readonly reducedMotion?: boolean;
  readonly onPotOfGreed?: (position: GridPosition) => void | Promise<void>;
}

export class FeatureDialogController implements FeatureOverlayPort {
  private readonly intro: HTMLDialogElement;
  private readonly award: HTMLOutputElement;
  private readonly summary: HTMLDialogElement;
  private readonly summaryFrame: HTMLElement;
  private readonly character: HTMLImageElement;
  private readonly eyebrow: HTMLElement;
  private readonly title: HTMLElement;
  private readonly total: HTMLOutputElement;
  private readonly copy: HTMLElement;
  private readonly summaryImages: FeatureSummaryImageLoader;
  private summaryTimer: ReturnType<typeof globalThis.setTimeout> | null = null;

  constructor(root: Document, private readonly options: FeatureDialogOptions = {}) {
    this.intro = requireElement(root, '[data-feature-intro-dialog]');
    this.award = requireElement(root, '[data-feature-award]');
    this.summary = requireElement(root, '[data-feature-summary-dialog]');
    this.summaryFrame = requireElement(this.summary, '[data-feature-summary-tone]');
    this.character = requireElement(this.summary, '[data-feature-character]');
    this.eyebrow = requireElement(this.summary, '[data-feature-eyebrow]');
    this.title = requireElement(this.summary, '[data-feature-title]');
    this.total = requireElement(this.summary, '[data-feature-total]');
    this.copy = requireElement(this.summary, '[data-feature-copy]');
    this.summaryImages = new FeatureSummaryImageLoader(
      Object.values(FEATURE_CHARACTER_URLS),
      () => root.createElement('img'),
    );
  }

  showIntro(awardedSpins: number): Promise<void> {
    this.award.value = String(Math.max(0, Math.trunc(awardedSpins)));
    if (!this.intro.open) this.intro.showModal();
    return this.waitForClose(this.intro);
  }

  async showRetrigger(awardedSpins: number): Promise<void> {
    const status = document.querySelector<HTMLElement>('[data-status-text]');
    if (status !== null) status.textContent = `Retrigger: +${Math.max(0, Math.trunc(awardedSpins))} Freispiele`;
    await new Promise<void>((resolve) => globalThis.setTimeout(resolve, this.options.reducedMotion === true ? 80 : 650));
  }

  async showPotOfGreed(position: GridPosition): Promise<void> {
    await this.options.onPotOfGreed?.(position);
  }

  async showSummary(totalWinCents: number, betCents: number): Promise<void> {
    const tier = getFeatureSummaryTier(totalWinCents, betCents);
    const characterUrl = FEATURE_CHARACTER_URLS[tier.character];
    if (!await this.summaryImages.apply(this.character, characterUrl)) return;
    this.summaryFrame.dataset.featureSummaryTone = tier.tone;
    this.character.alt = tier.character === 'joey' ? 'Joey Wheeler'
      : tier.character === 'seto' ? 'Seto Kaiba'
        : tier.character === 'marik' ? 'Marik Ishtar' : 'Solomon Muto';
    this.eyebrow.textContent = tier.eyebrow;
    this.title.textContent = tier.title;
    this.copy.textContent = tier.copy;
    this.total.value = formatCents(0);
    if (!this.summary.open) this.summary.showModal();
    if (this.summaryTimer !== null) globalThis.clearTimeout(this.summaryTimer);
    this.summaryTimer = globalThis.setTimeout(() => {
      this.summaryTimer = null;
      if (this.summary.open) this.summary.close();
    }, FEATURE_SUMMARY_AUTO_CLOSE_MS);
    await animateCentCount({
      fromCents: 0,
      toCents: totalWinCents,
      durationMs: tier.countUpMs,
      reducedMotion: this.options.reducedMotion,
      onUpdate: (value) => { this.total.value = formatCents(value); },
    });
    await this.waitForClose(this.summary);
  }

  destroy(): void {
    this.summaryImages.invalidate();
    if (this.summaryTimer !== null) globalThis.clearTimeout(this.summaryTimer);
    this.summaryTimer = null;
  }

  private waitForClose(dialog: HTMLDialogElement): Promise<void> {
    if (!dialog.open) return Promise.resolve();
    return new Promise((resolve) => dialog.addEventListener('close', () => resolve(), { once: true }));
  }
}
